/**
 * XML Parsing Service
 * Centralizes logic for parsing UBL (Universal Business Language) Invoices
 * and generic XML data.
 */

// UBL Namespaces
export const UBL_NAMESPACES = {
    CAC: "urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2",
    CBC: "urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2",
};

/**
 * Utility to get text content from a namespaced tag
 */
const getValue = (ns, tag, parent) =>
    parent?.getElementsByTagNameNS(ns, tag)[0]?.textContent?.trim() || "";

/**
 * Generates a random hex ID
 */
const getRandomHex = () =>
    Math.floor(Math.random() * 0xFFFFFF)
        .toString(16)
        .padStart(6, '0')
        .toUpperCase();

/**
 * Finds and parses embedded XML content inside CDATA or description fields
 */
export const findAndParseEmbeddedInvoice = (parser, xmlDoc, rawText) => {
    const { CBC } = UBL_NAMESPACES;
    try {
        const descNodes = xmlDoc.getElementsByTagNameNS(CBC, "Description");
        for (let i = 0; i < descNodes.length; i++) {
            const txt = descNodes[i].textContent?.trim();
            if (!txt) continue;
            if (txt.includes("<Invoice") || txt.startsWith("<?xml")) {
                const doc = parser.parseFromString(txt, "text/xml");
                if (doc.getElementsByTagName("parsererror")?.length === 0) {
                    return doc;
                }
            }
        }
        if (rawText && rawText.includes("<Invoice")) {
            const idx = rawText.indexOf("<Invoice");
            const candidate = rawText.substring(idx);
            const doc = parser.parseFromString(candidate, "text/xml");
            if (doc.getElementsByTagName("parsererror")?.length === 0) {
                return doc;
            }
        }
    } catch (err) {
        console.error("Error seeking/parsing embedded invoice:", err);
    }
    return null;
};

/**
 * Helper to extract main document tags (like ID, ParentDocumentID, UUID)
 * while ignoring child nodes inside UBLExtensions, InvoiceLine, PaymentMeans, etc.
 */
const extractMainTag = (doc, tagNames) => {
    if (!doc) return "";
    const root = doc.documentElement || doc;
    const tags = Array.isArray(tagNames) ? tagNames : [tagNames];

    // 1. Direct children of root element
    for (let i = 0; i < root.children.length; i++) {
        const child = root.children[i];
        const name = child.localName || child.tagName;
        if (tags.includes(name)) {
            const val = child.textContent?.trim();
            if (val) return val;
        }
    }

    // 2. Look for cbc:tag that is not inside UBLExtension, InvoiceLine, PaymentMeans, Party, etc.
    const { CBC } = UBL_NAMESPACES;
    for (const tag of tags) {
        const allNodes = Array.from(root.getElementsByTagNameNS(CBC, tag));
        for (const node of allNodes) {
            let p = node.parentNode;
            let skip = false;
            while (p && p !== root) {
                const pName = p.localName || p.tagName;
                if (["UBLExtension", "InvoiceLine", "CreditNoteLine", "DebitNoteLine", "PaymentMeans", "Party", "TaxScheme", "Item", "StandardItemIdentification", "SellersItemIdentification"].includes(pName)) {
                    skip = true;
                    break;
                }
                p = p.parentNode;
            }
            if (!skip) {
                const val = node.textContent?.trim();
                if (val) return val;
            }
        }
    }

    // 3. Fallback to any node matching tag
    for (const tag of tags) {
        const node = root.getElementsByTagNameNS(CBC, tag)[0] || root.getElementsByTagName(tag)[0];
        if (node?.textContent?.trim()) return node.textContent.trim();
    }

    return "";
};

/**
 * Parses a UBL Invoice XML document into a standardized object
 */
export const parseUblInvoice = (xmlContent) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, "text/xml");
    const { CAC, CBC } = UBL_NAMESPACES;

    const invoiceDoc = findAndParseEmbeddedInvoice(parser, xmlDoc, xmlContent) || xmlDoc;

    // Key Nodes
    const supplierNode = invoiceDoc.getElementsByTagNameNS(CAC, "AccountingSupplierParty")[0];
    const customerNode = invoiceDoc.getElementsByTagNameNS(CAC, "AccountingCustomerParty")[0];
    const invoiceLines = invoiceDoc.getElementsByTagNameNS(CAC, "InvoiceLine");

    const supplierParty = supplierNode?.getElementsByTagNameNS(CAC, "Party")[0] || supplierNode;
    const customerParty = customerNode?.getElementsByTagNameNS(CAC, "Party")[0] || customerNode;

    // Items extraction
    const items = Array.from(invoiceLines || []).map((line) => {
        const description = getValue(CBC, "Description", line) ||
            (line.getElementsByTagNameNS(CBC, "Description")[0]?.textContent ?? "");

        // Line Taxes
        const taxNode = line.getElementsByTagNameNS(CAC, "TaxTotal")[0];
        let taxAmount = "";
        let taxPercent = "";
        if (taxNode) {
            taxAmount = getValue(CBC, "TaxAmount", taxNode);
            const taxCategory = taxNode.getElementsByTagNameNS(CAC, "TaxCategory")[0];
            if (taxCategory) {
                taxPercent = getValue(CBC, "Percent", taxCategory);
            }
        }

        const quantity = parseFloat(getValue(CBC, "InvoicedQuantity", line) || "0");
        const subtotal = parseFloat(getValue(CBC, "LineExtensionAmount", line) || "0");
        const taxAmountValue = parseFloat(taxAmount || "0");
        const priceAmount = parseFloat(getValue(CBC, "PriceAmount", line) || getValue(CBC, "Price", line) || 0);
        const unit = line.getElementsByTagNameNS(CBC, "InvoicedQuantity")[0]?.getAttribute("unitCode") || "UND";

        const sku = getValue(CBC, "ID", line.getElementsByTagNameNS(CAC, "SellersItemIdentification")[0]) ||
            getValue(CBC, "ID", line.getElementsByTagNameNS(CAC, "StandardItemIdentification")[0]) || "";

        return {
            idx: getRandomHex(),
            description,
            quantity,
            unit,
            unit_price: priceAmount || (quantity > 0 ? subtotal / quantity : 0),
            tax: taxAmountValue,
            total: subtotal + taxAmountValue,
            subtotal: subtotal,
            taxRate: taxPercent || "0",
            sku,
        };
    });

    // Totals calculation
    const legalMonetary = invoiceDoc.getElementsByTagNameNS(CAC, "LegalMonetaryTotal")[0];
    const subtotal = (legalMonetary && getValue(CBC, "TaxExclusiveAmount", legalMonetary)) ||
        getValue(CBC, "LineExtensionAmount", invoiceDoc) ||
        getValue(CBC, "TaxExclusiveAmount", invoiceDoc) || "";

    const total = (legalMonetary && getValue(CBC, "PayableAmount", legalMonetary)) ||
        getValue(CBC, "PayableAmount", invoiceDoc) ||
        getValue(CBC, "TaxInclusiveAmount", invoiceDoc) || "";

    let tax = "";
    try {
        // Obtenemos solo los TaxTotal que son hijos directos del Invoice (Globales)
        // Evitamos sumar los TaxTotal de cada línea (InvoiceLine) que duplicarían el valor
        // Si no hay hijos directos, intentamos buscar el que NO está dentro de una InvoiceLine
        const allTaxTotals = Array.from(invoiceDoc.getElementsByTagNameNS(CAC, "TaxTotal"));
        const globalTaxTotals = allTaxTotals.filter(tNode => {
            let parent = tNode.parentNode;
            while (parent) {
                if (parent.localName === "InvoiceLine") return false;
                parent = parent.parentNode;
            }
            return true;
        });

        let sum = 0;
        globalTaxTotals.forEach(tNode => {
            const amt = tNode.getElementsByTagNameNS(CBC, "TaxAmount")[0];
            if (amt && amt.textContent) {
                sum += Number(amt.textContent.replace(",", ""));
            }
        });

        tax = sum ? String(sum) : getValue(CBC, "TaxAmount", invoiceDoc);
    } catch (err) {
        console.error("Error calculating global tax:", err);
        tax = getValue(CBC, "TaxAmount", invoiceDoc);
    }

    // Basic Info
    const id = extractMainTag(invoiceDoc, ["ParentDocumentID", "ID"]) || extractMainTag(xmlDoc, ["ParentDocumentID", "ID"]) || "";
    const issueDate = getValue(CBC, "IssueDate", invoiceDoc) || getValue(CBC, "IssueDate", xmlDoc);
    const issueTime = getValue(CBC, "IssueTime", invoiceDoc) || getValue(CBC, "IssueTime", xmlDoc);
    const cufe = extractMainTag(invoiceDoc, "UUID") || extractMainTag(xmlDoc, "UUID") || "";
    const currency = getValue(CBC, "DocumentCurrencyCode", invoiceDoc) || getValue(CBC, "DocumentCurrencyCode", xmlDoc) || "";

    // Parties info
    const supplierName = getValue(CBC, "RegistrationName", supplierParty) ||
        getValue(CBC, "Name", supplierParty) || "";

    const supplierNIT = getValue(CBC, "CompanyID", supplierParty) || "";

    const contactNode = supplierParty?.getElementsByTagNameNS(CAC, "Contact")[0];
    const supplierEmail = contactNode ? getValue(CBC, "ElectronicMail", contactNode) : "";
    const supplierPhone = contactNode ? getValue(CBC, "Telephone", contactNode) : "";

    const customerName = getValue(CBC, "RegistrationName", customerParty) ||
        getValue(CBC, "Name", customerParty) || "";

    const customerID = getValue(CBC, "CompanyID", customerParty) || "";

    // Payment info
    const paymentDueDate = getValue(CBC, "PaymentDueDate", invoiceDoc) ||
        getValue(CBC, "DueDate", invoiceDoc) || "";

    const paymentMeansCode = (() => {
        const paymentMeans = invoiceDoc.getElementsByTagNameNS(CAC, "PaymentMeans")[0];
        return paymentMeans ? getValue(CBC, "PaymentMeansCode", paymentMeans) : "";
    })();

    return {
        id,
        issueDate,
        issueTime,
        cufe,
        currency,
        supplier: supplierName,
        supplierEmail,
        supplierPhone,
        supplierNIT,
        customer: customerName,
        customerID,
        subtotal,
        tax,
        total,
        items,
        paymentDueDate,
        paymentMeansCode,
    };
};

/**
 * Generic XML to Object converter (used for Reports)
 */
export const xmlToTableData = (xmlContent) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

    const findRecordsNode = (xml) => {
        const children = Array.from(xml.children);
        for (let child of children) {
            const tagCounts = {};
            Array.from(child.children).forEach(c => {
                tagCounts[c.tagName] = (tagCounts[c.tagName] || 0) + 1;
            });
            if (Object.values(tagCounts).some(v => v > 1)) {
                return child;
            } else {
                const found = findRecordsNode(child);
                if (found) return found;
            }
        }
        return null;
    };

    const nodeToObject = (node) => {
        const obj = {};
        if (node.attributes) {
            for (let attr of Array.from(node.attributes)) {
                obj[attr.name] = attr.value;
            }
        }
        Array.from(node.children).forEach(child => {
            obj[child.tagName] = child.textContent;
        });
        if (node.children.length === 0 && node.textContent) {
            obj['value'] = node.textContent;
        }
        return obj;
    };

    const recordsNode = findRecordsNode(xmlDoc);
    if (!recordsNode) return null;

    return Array.from(recordsNode.children).map(nodeToObject);
};
